import React, { useEffect, useRef } from "react";
import { useTour } from '@reactour/tour';

export function buildIntroSteps({ hasMultipleProfiles }) {
  const raw = [
    hasMultipleProfiles && { selector: '#profile-select',     content: 'Select your profile here.' },
    { selector: '#grade-select',        content: 'Choose your grade level for appropriate evaluation.' },
    { selector: '#select-question-btn', content: '(Optional) Choose the essay question for relevant feedback.' },
    { selector: '#scan-essay-btn',      content: 'Scan your handwritten essay here, or type directly in the space below.' },
  ];
  return raw.filter(Boolean).filter(s => document.querySelector(s.selector));
}

export function buildAfterScanSteps() {
  return [
    { selector: '#submit-btn', content: 'Click here to submit your essay for evaluation.' },
  ].filter(s => document.querySelector(s.selector));
}

export function buildAfterSubmitSteps() {
  return [
    { selector: '#improve-essay-btn', content: 'View suggestions and fix issues step by step.' },
    { selector: '#try-again-btn',     content: 'Improve your essay and try again. Your previous version is kept.' },
    { selector: '#report-card-btn',   content: 'See all your past essay performance summaries in report card.' },
    { selector: '#share-btn',         content: 'Share your result with others. ' },
  ].filter(s => document.querySelector(s.selector));
}

export function openTour({ setSteps, setCurrent, setIsOpen }, steps) {
  if (!steps || !steps.length) return;
  setSteps(steps);
  setCurrent(0);
  setIsOpen(true);
}

export function useTargetHighlight() {
  const { isOpen, currentStep, steps } = useTour();
  const prevRef = React.useRef(null);

  React.useEffect(() => {
    // Clean previous highlight
    if (prevRef.current) {
      prevRef.current.classList.remove('ea-tour-highlight');
      prevRef.current = null;
    }

    if (!isOpen || !steps?.[currentStep]?.selector) return;

    const el = document.querySelector(steps[currentStep].selector);
    if (!el || !el.getClientRects().length || el.offsetParent === null) return;

    // Add highlight and ensure it's visible
    el.classList.add('ea-tour-highlight');
    prevRef.current = el;
    el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });

    // Cleanup on unmount/step change
    return () => {
      if (prevRef.current) {
        prevRef.current.classList.remove('ea-tour-highlight');
        prevRef.current = null;
      }
    };
  }, [isOpen, currentStep, steps]);
}

export const waitForEls = (selectors, timeout = 6000, interval = 100) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      const missing = selectors.filter(s => !document.querySelector(s));
      if (missing.length === 0) return resolve(true);
      if (Date.now() - start > timeout) 
        return reject(new Error(`Missing: ${missing.join(", ")}`));
      setTimeout(tick, interval);
    };
    tick();
  });

// waits until the elements' positions are stable across a few frames
export const waitForStableLayout = async (selectors, frames = 3, frameDelay = 120) => {
  const getRects = () =>
    selectors.map((s) => {
      const el = document.querySelector(s);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return [r.x, r.y, r.width, r.height].map(Math.round).join(",");
    });

  let prev = getRects();
  for (let i = 0; i < frames; i++) {
    await new Promise(r => setTimeout(r, frameDelay));
    const now = getRects();
    const changed = now.some((box, idx) => box !== prev[idx]);
    if (!changed) return true;
    prev = now;
  }
  return true;
};